import { CheckCircle, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { getMangoApiClient } from '~/atoms/MangoApi'
import { Alert, FontStyle, Input, Text } from '~/components/core'
import { MangoButton } from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePartnerLogo } from '~/hooks/useCurrentPartner'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  ContentWrapper,
  ForgotButton,
  ForgotButtonWrapper,
  InputWrapper,
  LoginButtonWrapper,
  LogoImage,
} from '~/pages/Login/Login.styles'
import { LoginBackground } from '~/pages/Login/LoginBackground'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { classifyPasswordResetRequest } from '~/utils/passwordResetAnalytics'

export const ForgotPasswordPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const partnerLogo = usePartnerLogo()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [networkError, setNetworkError] = useState(false)

  const handleSubmit = async () => {
    if (!email.trim()) return

    setIsLoading(true)
    setNetworkError(false)

    try {
      const api = getMangoApiClient()
      const response = await api.auth.requestPasswordReset(
        email.trim().toLowerCase(),
      )
      const result = classifyPasswordResetRequest(response)

      if (result.kind === 'failed') {
        track(AuthenticationAnalyticsEvents.PasswordResetFailed, {
          stage: 'request',
          reasonCode: result.reasonCode,
          status: result.status,
        })
        setNetworkError(true)
        return
      }

      track(AuthenticationAnalyticsEvents.PasswordResetRequested)
      setSubmitted(true)
    } catch {
      track(AuthenticationAnalyticsEvents.PasswordResetFailed, {
        stage: 'request',
        reasonCode: 'network',
      })
      setNetworkError(true)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSubmit()
    }
  }

  return (
    <LoginBackground>
      <ContentWrapper>
        <LogoImage alt="logo" src={partnerLogo} />
        {submitted ? (
          <>
            <Alert
              type="primary"
              icon={
                <CheckCircle
                  size={24}
                  color="var(--chakra-colors-brand-solid)"
                />
              }
            >
              {t(
                'forgotPassword.successMessage',
                "If an account exists for that email, we've sent a password reset link. Check your inbox.",
              )}
            </Alert>
            <LoginButtonWrapper>
              <MangoButton
                onClick={() => navigate(ApplicationRoutes.LOGIN)}
                size={Size.md}
              >
                {t('common.backToLogin', 'Back to login')}
              </MangoButton>
            </LoginButtonWrapper>
          </>
        ) : (
          <>
            {networkError && (
              <div style={{ width: '90%', marginBottom: '12px' }}>
                <Alert
                  icon={
                    <TriangleAlert
                      size={24}
                      color="var(--chakra-colors-red-solid)"
                    />
                  }
                >
                  {t(
                    'common.genericError',
                    'Something went wrong. Please try again.',
                  )}
                </Alert>
              </div>
            )}
            <div style={{ width: '90%', marginBottom: '16px' }}>
              <Text
                fontStyle={FontStyle.bSmall}
                text={t(
                  'forgotPassword.instructions',
                  "Enter your email address and we'll send you a link to reset your password.",
                )}
                color="var(--chakra-colors-fg-muted)"
              />
            </div>
            <InputWrapper>
              <Input
                id="email-input"
                placeholder={t(
                  'forgotPassword.emailPlaceholder',
                  'Email address',
                )}
                type="email"
                name="email"
                value={email}
                onChange={setEmail}
                onKeyDown={handleKeyDown}
              />
            </InputWrapper>
            <LoginButtonWrapper>
              <MangoButton
                onClick={() => void handleSubmit()}
                disabled={!email.trim() || isLoading}
                isLoading={isLoading}
                size={Size.md}
              >
                {t('forgotPassword.sendResetLink', 'Send reset link')}
              </MangoButton>
            </LoginButtonWrapper>
            <ForgotButtonWrapper>
              <ForgotButton onClick={() => navigate(ApplicationRoutes.LOGIN)}>
                <Text
                  fontStyle={FontStyle.bSmall}
                  text={t('common.backToLogin', 'Back to login')}
                  color="var(--chakra-colors-brand-solid)"
                />
              </ForgotButton>
            </ForgotButtonWrapper>
          </>
        )}
      </ContentWrapper>
    </LoginBackground>
  )
}
